import {
  LoaderScriptDataAttributes,
  Variant,
  type LoadWidgetProps,
  type VariantOptionOverrides,
} from '@asurion-core/widget-client/lib/package';
import {
  getContextNamespace,
  getLoaderNamespace,
  getLoaderReadyEventName,
  getPushNotificationNamespace,
  getTrackingParametersNamespace,
  getVariantOptionsNamespace,
} from '@asurion-core/widget-client/lib/utils';
import { createWidget } from './createWidget';
import { type LegacyActionQueue } from './types/LegacyActionQueue';

(async () => {
  const namespace = document.currentScript?.attributes.getNamedItem('id')?.value || '_si_widget';

  const autoLoad = Boolean(!document.currentScript?.hasAttribute(LoaderScriptDataAttributes.AUTO_LOAD_DISABLED));
  const showStateDebugger = Boolean(
    document.currentScript?.hasAttribute(LoaderScriptDataAttributes.SHOW_STATE_DEBUGGER)
  );

  const forceRestartOnInitAttribute =
    document.currentScript?.getAttribute(LoaderScriptDataAttributes.FORCE_RESTART_ON_INIT) ?? null;
  const forceRestartOnInit = forceRestartOnInitAttribute !== null && forceRestartOnInitAttribute !== 'false';

  if (autoLoad) {
    const actionQueue =
      (
        (window as unknown as Record<string, unknown>)[namespace] as {
          q: LegacyActionQueue;
        }
      )?.q ?? [];

    const [, legacyParams] = actionQueue.find(([action]) => action === 'init') || [];

    const id =
      document.currentScript?.getAttribute(LoaderScriptDataAttributes.WIDGET_ID) ??
      legacyParams?.id ??
      legacyParams?.variant;

    const flowSystemId = document.currentScript?.getAttribute(LoaderScriptDataAttributes.FLOW_SYSTEM_ID) ?? undefined;

    const parentElementId =
      document.currentScript?.getAttribute(LoaderScriptDataAttributes.PARENT_ELEMENT_ID) ?? undefined;

    const language =
      document.currentScript?.getAttribute(LoaderScriptDataAttributes.LANGUAGE) ||
      document.documentElement.lang ||
      navigator.language ||
      'en';

    if (!id) {
      throw new Error('Widget ID is not defined.');
    }

    const variant: Variant =
      (document.currentScript?.getAttribute(LoaderScriptDataAttributes.VARIANT) as Variant) ??
      legacyParams?.variant ??
      Variant.FLOATING_ACTION_CARD;

    const variantOptions =
      legacyParams?.variantOptionOverrides ??
      ((window as unknown as Record<string, unknown>)[getVariantOptionsNamespace(namespace)] as
        | VariantOptionOverrides
        | undefined) ??
      {};

    const context =
      ((window as unknown as Record<string, unknown>)[getContextNamespace(namespace)] as
        | LoadWidgetProps['context']
        | undefined) ?? {};

    const trackingParameters =
      ((window as unknown as Record<string, unknown>)[getTrackingParametersNamespace(namespace)] as
        | LoadWidgetProps['trackingParameters']
        | undefined) ?? {};

    const pushNotification = (window as unknown as Record<string, unknown>)[getPushNotificationNamespace(namespace)] as
      | LoadWidgetProps['pushNotification']
      | undefined;

    const variantOptionOverrides = legacyParams
      ? {
          [variant]: legacyParams,
          ...variantOptions,
        }
      : variantOptions;

    await createWidget({
      flowSystemId,
      id,
      namespace,
      variant,
      variantOptionOverrides,
      showStateDebugger,
      forceRestartOnInit,
      context,
      trackingParameters,
      pushNotification,
      parentElementId,
      language,
    });
  }

  (window as unknown as Record<string, unknown>)[getLoaderNamespace(namespace)] = createWidget;

  document.currentScript?.dispatchEvent(new CustomEvent(getLoaderReadyEventName(namespace)));
})();
